import { ReactNode, memo } from 'react';

import { ProgressBar } from '@backrs_ui/common-ui/src/atoms/ProgressBar';
import { getMarkdownComponents } from '@backrs_ui/common-ui/src/molecules/Markdown/markdownComponents';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { letterFor } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/optionLetters';
import { layOutPassageFigures } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/passageFigures';
import {
  DailyQuestion,
  DailyQuestionFigure,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, Button, Divider, SxProps, Theme, Typography } from '@mui/material';
import ReactMarkdown, { Components } from 'react-markdown';

import {
  QuestionMarkdown,
  markdownContentSx,
  questionRehypePlugins,
  questionRemarkPlugins,
  testSerifSx,
} from './QuestionMarkdown';
import { QuizFigure } from './QuizFigure';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.quiz;

type QuizWizardProps = Readonly<{
  question: DailyQuestion;
  index: number;
  total: number;
  /** The highlighted option: the tentative pick before submit, or the locked answer after. Null while nothing is picked. */
  selectedOptionIndex: number | null;
  /** Once submitted, options lock and reveal correct/incorrect; the CTA switches from Submit to Next. */
  submitted: boolean;
  onSelect: (optionIndex: number) => void;
  onSubmit: () => void;
  onBack: () => void;
  onNext: () => void;
  canBack: boolean;
  isLast: boolean;
  /** Blocks Next while the finished quiz is being saved, so the attempt can't be sent twice. */
  nextDisabled?: boolean;
  /** Post-submit feedback, placed above the nav row so it precedes the control that leaves it. */
  feedback?: ReactNode;
}>;

type OptionState = 'idle' | 'selected' | 'correct' | 'incorrect' | 'muted';

const optionState = (submitted: boolean, isCorrect: boolean, isSelected: boolean): OptionState => {
  if (!submitted) return isSelected ? 'selected' : 'idle';
  if (isCorrect) return 'correct';

  return isSelected ? 'incorrect' : 'muted';
};

// A <button> may hold only phrasing content, so unwrap <p> to its inline children and drop links.
const optionMarkdownComponents: Components = {
  ...getMarkdownComponents('LARGE'),
  p: ({ children }) => <>{children}</>,
};

const OptionMarkdown = memo(({ content }: Readonly<{ content: string }>) => (
  <ReactMarkdown
    remarkPlugins={questionRemarkPlugins}
    rehypePlugins={questionRehypePlugins}
    components={optionMarkdownComponents}
    disallowedElements={['a']}
    unwrapDisallowed
  >
    {content}
  </ReactMarkdown>
));

const figureAlt = (figure: DailyQuestionFigure) => figure.label ?? STRINGS.questionFigureAlt;

/** An ordered run of figures. */
const FigureStack = ({ figures }: Readonly<{ figures: readonly DailyQuestionFigure[] }>) => (
  <>
    {figures.map((figure) => (
      <QuizFigure key={figure.url} url={figure.url} size="LARGE" alt={figureAlt(figure)} />
    ))}
  </>
);

/**
 * The passage with each figure standing where it prints that figure's caption. The image
 * replaces the caption line, carrying the same caption in its own pixels.
 */
const PassageBody = ({
  passage,
  figures,
}: Readonly<{ passage: string; figures: readonly DailyQuestionFigure[] }>) => {
  const { segments, trailing } = layOutPassageFigures(passage, figures);

  return (
    <>
      {segments.map((segment, i) =>
        segment.kind === 'figure' ? (
          <QuizFigure
            key={segment.figure.url}
            url={segment.figure.url}
            size="LARGE"
            alt={figureAlt(segment.figure)}
          />
        ) : (
          <QuestionMarkdown key={`prose-${i}`} content={segment.content} size="LARGE" />
        ),
      )}
      <FigureStack figures={trailing} />
    </>
  );
};

/** One-question-at-a-time quiz card: progress, passage + stem, single-select options, Submit → Next. */
export const QuizWizard = ({
  question,
  index,
  total,
  selectedOptionIndex,
  submitted,
  onSelect,
  onSubmit,
  onBack,
  onNext,
  canBack,
  isLast,
  nextDisabled = false,
  feedback,
}: QuizWizardProps) => {
  const stem = (
    <Typography variant="text-md-regular" component="div" sx={stemSx} id="booster-quiz-stem">
      <QuestionMarkdown content={question.stem} size="LARGE" />
    </Typography>
  );

  return (
    <Box sx={cardSx} id="booster-quiz-card">
      <Box sx={progressRowSx}>
        <Typography variant="text-lg-semibold" sx={progressLabelSx}>
          {STRINGS.progress(index + 1, total)}
        </Typography>
        <Box sx={progressBarSx}>
          <ProgressBar progress={index + 1} total={total} />
        </Box>
      </Box>

      {/* Full-bleed separator between the progress row and the question. */}
      <Divider sx={progressDividerSx} />

      {question.passage ? (
        <>
          <Typography variant="text-md-regular" component="div" sx={passageSx}>
            <PassageBody passage={question.passage} figures={question.figures} />
          </Typography>
          {stem}
        </>
      ) : (
        <Box sx={stemFrameSx}>
          <FigureStack figures={question.figures} />
          {stem}
        </Box>
      )}

      <Box sx={optionsSx} role="radiogroup" aria-labelledby="booster-quiz-stem">
        {question.options.map((option, optionIndex) => {
          const isSelected = selectedOptionIndex === optionIndex;
          const state = optionState(submitted, option.isCorrect, isSelected);
          const letter = letterFor(optionIndex);

          return (
            <Box
              component="button"
              type="button"
              role="radio"
              aria-checked={isSelected}
              key={option.id}
              id={`booster-quiz-option-${optionIndex}`}
              disabled={submitted}
              onClick={() => onSelect(optionIndex)}
              sx={optionSx(state)}
            >
              <Typography variant="text-sm-semibold" component="span" sx={letterBoxSx(state)}>
                {letter}
              </Typography>
              <Typography variant="text-md-medium" component="div" sx={optionTextSx(state)}>
                <OptionMarkdown content={option.text} />
                <QuizFigure
                  url={option.mediaUrl}
                  size="LARGE"
                  alt={STRINGS.optionFigureAlt(letter)}
                />
              </Typography>
            </Box>
          );
        })}
      </Box>

      {feedback}

      <Box sx={navSx}>
        <Button
          id="booster-quiz-back-btn"
          hierarchy="Secondary gray"
          size="lg"
          disabled={!canBack}
          onClick={onBack}
          sx={navButtonSx}
        >
          {STRINGS.back}
        </Button>
        {submitted ? (
          <Button
            id="booster-quiz-next-btn"
            hierarchy="Primary"
            size="lg"
            disabled={nextDisabled}
            onClick={onNext}
            sx={navButtonSx}
          >
            {isLast ? STRINGS.seeResults : STRINGS.next}
          </Button>
        ) : (
          <Button
            id="booster-quiz-submit-btn"
            hierarchy="Primary"
            size="lg"
            disabled={selectedOptionIndex === null}
            onClick={onSubmit}
            sx={navButtonSx}
          >
            {STRINGS.submit}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  paddingInline: backrs.spacing['spacing-3xl'],
  paddingBlock: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Beige/950']}`,
  borderRadius: backrs.radius['radius-xl'],
  boxShadow: backrs.shadows['shadow-xs'],
});

// Cancels the card's inline padding so the rule meets both edges.
const progressDividerSx: SxProps<Theme> = ({ backrs }) => ({
  marginInline: `calc(-1 * ${backrs.spacing['spacing-3xl']})`,
  borderColor: backrs.colors['Beige/950'],
});

const progressRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const progressLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  whiteSpace: 'nowrap',
});

const progressBarSx: SxProps<Theme> = {
  flex: 1,
};

// Every question has exactly one framed block: whatever the student is reading.
const questionFrame = (backrs: Theme['backrs']) => ({
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
});

// Scrolls both axes — markdown table cells are nowrap, so a wide passage overflows horizontally.
const passageSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  ...questionFrame(backrs),
  color: backrs.colors['Colors/Text/text-primary'],
  maxHeight: '320px',
  overflow: 'auto',
});

const stemFrameSx: SxProps<Theme> = ({ backrs }) => ({
  ...questionFrame(backrs),
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const stemSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  color: backrs.colors['Colors/Text/text-primary'],
});

const optionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const optionSx =
  (state: OptionState): SxProps<Theme> =>
  ({ backrs }) => {
    const border =
      state === 'selected'
        ? backrs.colors['Colors/Border/border-brand']
        : state === 'correct'
          ? backrs.colors['Colors/Foreground/fg-success-secondary']
          : state === 'incorrect'
            ? backrs.colors['Colors/Border/border-error']
            : backrs.colors['Colors/Border/border-secondary'];
    const bg =
      state === 'correct'
        ? backrs.colors['Colors/Background/bg-success-primary']
        : state === 'incorrect'
          ? backrs.colors['Colors/Background/bg-error-primary']
          : backrs.colors['Colors/Background/bg-primary'];
    const idle = state === 'idle' || state === 'muted';
    // The emphasis (selected/answered, and idle-on-hover) is drawn as an inset ring rather than a
    // thicker border, so the box never resizes between states — the option's contents stay put.
    const hoverColor = idle ? backrs.colors['Colors/Border/border-brand'] : border;

    return {
      display: 'flex',
      alignItems: 'flex-start',
      gap: backrs.spacing['spacing-lg'],
      width: '100%',
      textAlign: 'left',
      padding: backrs.spacing['spacing-xl'],
      border: `1px solid ${border}`,
      borderRadius: backrs.radius['radius-xl'],
      backgroundColor: bg,
      boxShadow: idle ? 'none' : `inset 0 0 0 1px ${border}`,
      cursor:
        state === 'correct' || state === 'incorrect' || state === 'muted' ? 'default' : 'pointer',
      opacity: state === 'muted' ? 0.6 : 1,
      transition:
        'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background-color 0.15s ease-in-out',
      '&:hover': {
        borderColor: hoverColor,
        boxShadow: `inset 0 0 0 1px ${hoverColor}`,
      },
      '&.Mui-disabled, &:disabled': {
        cursor: 'default',
      },
    };
  };

const letterBoxSx =
  (state: OptionState): SxProps<Theme> =>
  ({ backrs, typography }) => {
    const deemphasized = state === 'idle' || state === 'muted';
    const fill = deemphasized
      ? backrs.colors['Colors/Base/White']
      : state === 'correct'
        ? backrs.colors['Colors/Foreground/fg-success-primary']
        : state === 'incorrect'
          ? backrs.colors['Colors/Foreground/fg-error-primary']
          : backrs.colors['Brand/500'];
    // Centers the box on the option text's first line.
    const size = typography['text-md-medium']?.lineHeight;

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: backrs.radius['radius-sm'],
      border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
      backgroundColor: fill,
      color: deemphasized
        ? backrs.colors['Colors/Text/text-secondary']
        : backrs.colors['Colors/Text/text-white'],
    };
  };

const optionTextSx =
  (state: OptionState): SxProps<Theme> =>
  ({ backrs }) => ({
    ...testSerifSx,
    color:
      state === 'idle' || state === 'muted'
        ? backrs.colors['Colors/Text/text-secondary']
        : backrs.colors['Colors/Text/text-primary'],
  });

const navSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  marginTop: backrs.spacing['spacing-4xl'],
});

const navButtonSx: SxProps<Theme> = {
  flex: 1,
};
